// New Estimate wizard — Feb 2026 · iter49.
//
// P0 blocker repair: iter43 shipped a 3-step wizard but left the voice
// recorder as a UI stub (no MediaRecorder, no Whisper, no transcript).
// That stub is now replaced with a real recorder that:
//   1. Requests mic permission the moment the contractor clicks Record.
//   2. Only switches to "Stop" once recording *actually* started.
//   3. Uploads the audio blob to the existing
//      POST /api/voice-notes/upload?project_id={id}&source=recorded
//      endpoint (which requires a materialized draft project).
//   4. Auto-transcribes via POST /api/voice-notes/{id}/transcribe.
//   5. Appends the transcript into ``form.voice_transcript`` so the
//      wizard's downstream PUT /api/projects/{id} saves it into the
//      project record — which POST /api/ai/organize-scope reads.
//
// Also fixes:
//   * Wizard state (form + step + draftProjectId) now survives browser
//     back/forward + refresh via sessionStorage. Duplicate project /
//     estimate creation is prevented by keeping the draftProjectId.
//   * Visible in-app Back buttons at the top of Steps 2 & 3.
//   * beforeunload warning when a draft has meaningful content.
//   * Actionable diagnostics for every failure branch (mic denied,
//     recording failed, transcription failed, project save failed).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, explainSaveError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import {
  ArrowLeft, ArrowRight, Mic, Loader2, Square, Check, Camera,
  ChevronDown, ChevronUp, Settings2, AlertTriangle, Radio,
} from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import LeviIntakeCoach from "@/components/levi/LeviIntakeCoach";
import VideoWalkRecorder from "@/components/videoWalk/VideoWalkRecorder";

const STEPS = [
  { key: "customer", label: "Customer + Job Basics" },
  { key: "walk",     label: "Scope / Job Walk Notes" },
  { key: "review",   label: "Review + Create Estimate" },
];

const DRAFT_KEY = "jc:new-estimate-draft-v2";
const AUDIO_MAX_MB = 25;

// A `form` shape that keys `useState` for the whole wizard. Keeping this in
// one place lets us hydrate cleanly from sessionStorage without dropping
// fields the backend expects on PUT /projects/{id}.
const EMPTY_FORM = {
  customer_name: "", customer_phone: "", customer_email: "",
  job_address: "", job_type: "", job_description: "",
  voice_transcript: "", typed_notes: "", measurements: "",
  material_notes: "", labor_notes: "", timeline: "",
  special_conditions: "", customer_concerns: "",
  urgency: "", service_needed: "", intake_access_notes: "",
  insurance_claim: "", insurance_carrier: "", insurance_claim_number: "",
  preferred_appointment_at: "", lead_source: "",
};

const Field = ({ label, ...rest }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{label}</label>
    <input {...rest} className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]" />
  </div>
);
const TA = ({ label, rows = 3, ...rest }) => (
  <div>
    <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">{label}</label>
    <textarea {...rest} rows={rows} className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]" />
  </div>
);

const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeDraft = (payload) => {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be blocked in embedded contexts — non-fatal.
  }
};

const clearDraft = () => {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* noop */
  }
};

const formHasContent = (form) =>
  Boolean(
    (form.customer_name || "").trim() ||
    (form.job_description || "").trim() ||
    (form.voice_transcript || "").trim() ||
    (form.typed_notes || "").trim() ||
    (form.measurements || "").trim()
  );

const NewEstimate = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useLang();
  // PO 2026-02 · Project-scoped Vision auto-start parity.
  // When Levi routes an "Activate Vision" voice intent inside an
  // existing project AND the frontend Vision-conversational flag is
  // OFF, ``useLeviCommand`` navigates here with ``?levi_camera=1`` (see
  // ``useLeviCommand.js:1526-1528``). We read the flag here and thread
  // it into the Step-3 VideoWalkRecorder mount as ``autoStart`` so the
  // recorder's already-accepted one-shot ``_autoStartAttemptedRef``
  // effect fires exactly once for the same user-gesture chain that
  // opened the drawer. When the contractor arrived via a manual click
  // (no ``levi_camera`` param) the value is false and no auto-start
  // fires — the existing manual "Start Video Job Walk" button
  // remains fully functional. Zero regression to non-voice entry.
  const _autoCamera = params.get("levi_camera") === "1";

  const bootstrap = useMemo(() => readDraft() || {}, []);
  const [step, setStep] = useState(
    typeof bootstrap.step === "number" ? bootstrap.step : 0,
  );
  // Slice 2J correction · Job Notes & Measurements combined field.
  // On draft hydration, if the stale draft still carries a separate
  // `measurements` value (from before this correction), roll it
  // forward into `typed_notes` and clear `measurements` so the
  // combined textarea in Step 2 sees the full content. Never
  // duplicates: if `typed_notes` already contains the measurements
  // substring the measurements value is dropped silently. The write
  // path on submit persists `typed_notes` as the source of truth and
  // `measurements` as "" so the backend field still round-trips but
  // no longer competes with typed_notes for contractor entry.
  const initialForm = useMemo(() => {
    const merged = { ...EMPTY_FORM, ...(bootstrap.form || {}) };
    const rawM = (merged.measurements || "").trim();
    const rawN = (merged.typed_notes || "").trim();
    if (!rawM) return merged;
    if (rawN.includes(rawM)) {
      return { ...merged, measurements: "" };
    }
    return {
      ...merged,
      typed_notes: rawN ? `${rawM}\n\n${rawN}` : rawM,
      measurements: "",
    };
  }, [bootstrap]);
  const [form, setForm] = useState(initialForm);
  const [draftProjectId, setDraftProjectId] = useState(bootstrap.draftProjectId || null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  // iter57 · Slice 1B — if launched from a Job Detail page, carry the
  // Job id through the create flow so the resulting estimate is linked.
  const jobIdRef = useRef(params.get("job_id") || "");

  // Slice 2J amendment · Universal Edit Estimate. When the wizard is
  // opened with ?edit_estimate=<eid>, hydrate the form from the
  // existing project + estimate, show "Edit Estimate" as the page
  // heading, save changes back to the SAME project (PUT) and estimate,
  // then return to /app/estimates/<eid>?levi_ack=1 so the centered
  // Levi acknowledgement fires. No new editor is created; the existing
  // intake page is reused. Estimate identity is preserved.
  const editEstimateId = params.get("edit_estimate") || "";
  const projectPrefillId = params.get("project") || "";
  const isEditMode = Boolean(editEstimateId && projectPrefillId);
  const editHydratedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || editHydratedRef.current) return;
    editHydratedRef.current = true;
    (async () => {
      try {
        const [p, e] = await Promise.all([
          api.get(`/projects/${projectPrefillId}`),
          api.get(`/estimates/${editEstimateId}`),
        ]);
        const proj = p.data || {};
        const est = e.data || {};
        // Apply the same one-shot combined-field migration on
        // hydration so legacy `measurements` roll forward into
        // `typed_notes` and the contractor sees one combined field.
        const rawM = (proj.measurements || "").trim();
        const rawN = (proj.typed_notes || "").trim();
        const migratedNotes = !rawM
          ? rawN
          : rawN.includes(rawM)
            ? rawN
            : rawN
              ? `${rawM}\n\n${rawN}`
              : rawM;
        setForm((prev) => ({
          ...prev,
          customer_name: proj.customer_name || prev.customer_name,
          customer_phone: proj.customer_phone || prev.customer_phone,
          customer_email: proj.customer_email || prev.customer_email,
          job_address: proj.job_address || prev.job_address,
          job_type: proj.job_type || prev.job_type,
          job_description: proj.job_description || prev.job_description,
          voice_transcript: proj.voice_transcript || prev.voice_transcript,
          typed_notes: migratedNotes,
          measurements: "",
          material_notes: proj.material_notes || prev.material_notes,
          labor_notes: proj.labor_notes || prev.labor_notes,
          timeline: proj.timeline || prev.timeline,
          special_conditions: proj.special_conditions || prev.special_conditions,
          customer_concerns: proj.customer_concerns || prev.customer_concerns,
        }));
        setDraftProjectId(proj.id);
        setStep(1);   // Skip Step 0 (customer basics already captured).
      } catch (err) {
        toast.error("Couldn't load estimate for editing.");
      }
    })();
  }, [isEditMode, projectPrefillId, editEstimateId]);

  // iter57 · Slice 1B — one-shot prefill from query params when the user
  // arrived here from a Job Detail "Create Estimate" button. We only
  // apply this on the very first render and only when there is no
  // in-progress draft (respect the resume-later contract).
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;
    const jid = params.get("job_id");
    if (!jid) return;
    // Do not overwrite an existing in-flight draft (contract: resume-later).
    if (bootstrap && bootstrap.form && (
      (bootstrap.form.customer_name || "").trim() ||
      (bootstrap.form.voice_transcript || "").trim() ||
      (bootstrap.form.typed_notes || "").trim()
    )) return;
    setForm((prev) => ({
      ...prev,
      customer_name: params.get("customer_name") || prev.customer_name,
      customer_phone: params.get("customer_phone") || prev.customer_phone,
      customer_email: params.get("customer_email") || prev.customer_email,
      job_address: params.get("job_address") || prev.job_address,
      job_type: params.get("job_type") || prev.job_type,
    }));
  }, []);

  // Voice recording state — all live handles kept in refs so we don't trigger
  // spurious rerenders every audio chunk.
  const [recStatus, setRecStatus] = useState("idle"); // idle | requesting | recording | uploading | transcribing
  const [recError, setRecError] = useState("");
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  // iter52 · Live Dictation (Web Speech API) — restored to be the PRIMARY
  // voice-input path. Records interim + final SpeechRecognition results
  // directly into the same voice_transcript textarea the contractor edits
  // (matches the pre-iter43 baseline from commit 146a907). Runs
  // alongside — never replaces — the recorded-audio + Whisper backup
  // flow above (which is now visually secondary).
  const [dictStatus, setDictStatus] = useState("idle"); // idle | listening | error
  const [dictError, setDictError] = useState("");
  const [recorderOpen, setRecorderOpen] = useState(false); // Secondary panel disclosure.
  const dictRecognitionRef = useRef(null);
  // Baseline: whatever text lived in the textarea when the contractor
  // clicked Start Dictation. NEVER changes during a session.
  const dictBaselineRef = useRef("");
  // Committed final chunks accumulated across this session. Grows on
  // every event that has ``isFinal`` results.
  const dictFinalRef = useRef("");
  // Watchdog timer — cleared on the first event; fires when the browser
  // enters listening state but no recognition events arrive
  // (silent-hang caused by Chromium builds without a speech backend,
  // firewalled Google speech endpoint, or a permission dialog never
  // acted on).
  const dictWatchdogRef = useRef(null);
  const dictReceivedEventRef = useRef(false);
  const dictSupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  }, []);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canNext = () => (step !== 0 ? true : form.customer_name.trim().length > 0);

  // Persist every meaningful state change to sessionStorage so refresh /
  // browser Back / Forward do not vaporize the draft. We intentionally
  // keep this a plain effect that fires per render (guarded by useEffect
  // dependency array) — the payload is tiny.
  useEffect(() => {
    if (submittedRef.current) return;
    writeDraft({ step, form, draftProjectId });
  }, [step, form, draftProjectId]);

  // beforeunload warning. We only bark when the draft has meaningful
  // content that has NOT yet been promoted into an estimate (i.e. we
  // still have wizard state in memory / sessionStorage).
  useEffect(() => {
    const handler = (e) => {
      if (submittedRef.current) return;
      if (!formHasContent(form)) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [form]);

  // Cleanup any open mic stream + recognizer + watchdog when leaving.
  useEffect(() => {
    return () => {
      try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch { /* noop */ }
      try { dictRecognitionRef.current?.stop?.(); } catch { /* noop */ }
      if (dictWatchdogRef.current) {
        clearTimeout(dictWatchdogRef.current);
        dictWatchdogRef.current = null;
      }
    };
  }, []);

  // ---------------- Live Dictation (Web Speech API) — PRIMARY ----------------
  // Behavior contract (matches the last known working implementation from
  // commit 146a907, ported into the 3-step wizard):
  //   * Interim + final SpeechRecognition results stream directly into the
  //     voice_transcript textarea via setForm — the contractor sees words
  //     appear as they speak, exactly like typing by voice.
  //   * Finalized text is committed to dictFinalRef so subsequent interim
  //     buffers only rewrite the trailing edit tail — previously finalized
  //     words never disappear, duplicate, or reorder.
  //   * The baseline (whatever was already in the textarea at Start) is
  //     preserved on top; a re-start appends after a blank line.
  //   * A ~9 s watchdog kills a session that entered listening but never
  //     received any recognition event — no more indefinite "Listening…".

  const DICT_WATCHDOG_MS = 9000;
  const _dictClearWatchdog = () => {
    if (dictWatchdogRef.current) {
      clearTimeout(dictWatchdogRef.current);
      dictWatchdogRef.current = null;
    }
  };
  const _dictMergedText = () => {
    const parts = [dictBaselineRef.current, dictFinalRef.current]
      .filter(Boolean);
    // Baseline uses paragraph separation to keep prior content visually
    // grouped; committed + interim within this session are joined by a
    // single space.
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0];
    return parts[0] + "\n\n" + parts[1];
  };

  const _dictWriteToForm = (interimSuffix = "") => {
    const committed = _dictMergedText();
    // Interim tail lives on the same line as the trailing committed
    // text so it visually reads like the contractor is still typing.
    const tail = interimSuffix
      ? (committed ? committed + (dictFinalRef.current ? " " : "\n\n") : "") + interimSuffix
      : committed;
    setForm((prev) => ({ ...prev, voice_transcript: tail }));
  };

  const stopDictation = () => {
    _dictClearWatchdog();
    const rec = dictRecognitionRef.current;
    if (!rec) {
      setDictStatus("idle");
      return;
    }
    try { rec.stop(); } catch { /* onend still fires */ }
  };

  const startDictation = () => {
    setDictError("");
    if (dictStatus === "listening") return;
    if (!dictSupported) {
      const msg = "Live Dictation isn't supported by this browser. Use the latest Chrome or Edge on desktop, or type your notes below.";
      setDictError(msg);
      toast.error(msg);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    let recognition;
    try {
      recognition = new SR();
    } catch (initErr) {
      // Log detail for engineering without leaking to UI.
      console.warn("[dictation] SpeechRecognition constructor threw:", initErr?.name || initErr);
      setDictError("Live Dictation could not initialize in this browser.");
      toast.error("Live Dictation could not initialize.");
      return;
    }
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Snapshot state for this session.
    dictBaselineRef.current = (form.voice_transcript || "").trim();
    dictFinalRef.current = "";
    dictReceivedEventRef.current = false;

    recognition.onresult = (event) => {
      // Any result — interim or final — proves the browser backend is
      // alive; clear the watchdog.
      dictReceivedEventRef.current = true;
      _dictClearWatchdog();

      let interimBuf = "";
      let newFinal = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        const t = r[0]?.transcript || "";
        if (r.isFinal) newFinal += (newFinal ? " " : "") + t.trim();
        else interimBuf += t;
      }
      if (newFinal) {
        dictFinalRef.current = (
          (dictFinalRef.current ? dictFinalRef.current + " " : "") + newFinal
        ).trim();
      }
      _dictWriteToForm(interimBuf.trim());
    };

    recognition.onerror = (ev) => {
      const code = ev?.error || "unknown";
      _dictClearWatchdog();
      // ``aborted`` fires when the contractor (or our stop button) ends
      // the session cleanly — treat as a normal stop, not an error.
      if (code === "aborted") {
        dictRecognitionRef.current = null;
        setDictStatus("idle");
        return;
      }
      // Diagnostic detail goes to console (no transcript / no customer
      // data) — UI stays plain-English.
      console.warn("[dictation] recognition error:", code);
      const msg =
        code === "not-allowed" || code === "service-not-allowed"
          ? "Microphone permission denied. Enable mic access for this site, then click Start Dictation again."
          : code === "audio-capture"
          ? "We couldn't reach your microphone. Check that the correct mic is selected in your browser and no other app is holding it, then try again."
          : code === "no-speech"
          ? "No speech detected. Try again in a quieter spot, or type your notes below."
          : code === "network"
          ? "Live Dictation lost its network connection. Reconnect and try again, or type your notes below."
          : `Live Dictation stopped (${code}). Try again, or type your notes below.`;
      // Preserve the transcript we already have — commit any last interim.
      _dictWriteToForm("");
      setDictError(msg);
      setDictStatus("idle");
      toast.error(msg);
      dictRecognitionRef.current = null;
    };

    recognition.onend = () => {
      _dictClearWatchdog();
      // Commit any lingering interim into final on end so the field is
      // stable when the recognizer auto-stops on silence.
      _dictWriteToForm("");
      setDictStatus((prev) => (prev === "listening" ? "idle" : prev));
      dictRecognitionRef.current = null;
    };

    try {
      recognition.start();
    } catch (startErr) {
      console.warn("[dictation] start() threw:", startErr?.name || startErr);
      setDictError("Live Dictation could not start. Try again, or type your notes below.");
      toast.error("Live Dictation could not start.");
      return;
    }
    dictRecognitionRef.current = recognition;
    setDictStatus("listening");
    toast.info("Live Dictation started — words appear as you speak.");

    // Watchdog: if the browser accepts .start() but never fires any
    // event (Chromium-derived builds without Google's speech backend,
    // firewalled endpoints, unattended permission prompt), we cannot
    // leave the contractor stuck in "Listening…" forever.
    _dictClearWatchdog();
    dictWatchdogRef.current = setTimeout(() => {
      if (dictReceivedEventRef.current) return;
      const rec = dictRecognitionRef.current;
      if (rec) {
        try { rec.stop(); } catch { /* noop */ }
      }
      const msg = "We aren't receiving speech from your browser. Check microphone permission and use the latest Chrome or Edge, or type your notes below.";
      _dictWriteToForm(""); // preserve any prior transcript
      setDictError(msg);
      setDictStatus("idle");
      toast.error(msg);
      dictRecognitionRef.current = null;
      dictWatchdogRef.current = null;
    }, DICT_WATCHDOG_MS);
  };

  // ---------------- Project draft helpers ----------------

  const ensureDraftProject = async () => {
    if (draftProjectId) {
      try {
        await api.put(`/projects/${draftProjectId}`, { ...form, status: "draft" });
      } catch {
        /* non-fatal — we'll retry on the next transition */
      }
      return draftProjectId;
    }
    const { data: project } = await api.post("/projects", { ...form, status: "draft" });
    setDraftProjectId(project.id);
    return project.id;
  };

  const goNext = async () => {
    if (step === 0) {
      try {
        await ensureDraftProject();
      } catch (err) {
        toast.error(explainSaveError(err, "project"));
        return;
      }
    } else if (draftProjectId) {
      try {
        await api.put(`/projects/${draftProjectId}`, { ...form, status: "draft" });
      } catch {
        /* non-fatal */
      }
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep(Math.max(0, step - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---------------- Voice recording ----------------

  const startRecording = async () => {
    setRecError("");
    if (recStatus !== "idle") return;

    // The uploader requires a materialized project so the transcript can
    // attach cleanly. Force the draft to exist before we open the mic.
    let pid = draftProjectId;
    if (!pid) {
      if (!form.customer_name.trim()) {
        setRecError("Add a customer name in Step 1 first — we need a project to attach the recording to.");
        toast.error("Add a customer name in Step 1 first — we need a project to attach the recording to.");
        return;
      }
      try {
        pid = await ensureDraftProject();
      } catch (err) {
        setRecError("Could not create a draft project for the recording. Try again in a moment.");
        toast.error(explainSaveError(err, "draft project"));
        return;
      }
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setRecError("Recording is not supported in this browser. Please use a recent Chrome or Edge, or paste the walkthrough into the transcript box below.");
      toast.error("Recording is not supported in this browser.");
      return;
    }

    setRecStatus("requesting");
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const msg = err?.name === "NotAllowedError"
        ? "Microphone permission denied. Enable mic access for this site in your browser settings, then try again."
        : err?.name === "NotFoundError"
        ? "No microphone detected on this device. Plug one in, or type the walkthrough below."
        : "Could not start the microphone. Try again, or type the walkthrough below.";
      setRecStatus("idle");
      setRecError(msg);
      toast.error(msg);
      return;
    }

    // Choose the most-supported mime type in a Chrome/Edge-first order so
    // Whisper always has something to work with.
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    const mime = candidates.find((m) => {
      try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
    }) || "";

    let mr = null;
    try {
      mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (err) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      setRecStatus("idle");
      setRecError("Could not initialize the recorder. Try again or type the walkthrough below.");
      toast.error("Could not initialize the recorder.");
      return;
    }

    streamRef.current = stream;
    mediaRecorderRef.current = mr;
    chunksRef.current = [];

    mr.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    mr.onerror = () => {
      setRecStatus("idle");
      setRecError("Audio capture stopped unexpectedly. Try again, or type the walkthrough below.");
      toast.error("Audio capture stopped unexpectedly.");
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
      mediaRecorderRef.current = null;
    };
    mr.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
      mediaRecorderRef.current = null;

      if (!blob || blob.size === 0) {
        setRecStatus("idle");
        setRecError("The recorder produced no audio. Please try again — make sure your mic is unmuted.");
        toast.error("Recording captured no audio. Please try again.");
        return;
      }
      if (blob.size > AUDIO_MAX_MB * 1024 * 1024) {
        setRecStatus("idle");
        setRecError(`Recording is over the ${AUDIO_MAX_MB} MB limit. Try a shorter clip or paste the notes.`);
        toast.error(`Recording exceeds ${AUDIO_MAX_MB} MB.`);
        return;
      }

      // iter50 · Pick a filename extension that actually matches the
      // MediaRecorder's mimeType so the server-side content-type sniff and
      // any downstream toolchain (e.g. Whisper) see a consistent envelope.
      const rawType = (mr.mimeType || "audio/webm").toLowerCase();
      const ext = rawType.includes("mp4") ? "mp4"
        : rawType.includes("ogg") ? "ogg"
        : rawType.includes("wav") ? "wav"
        : "webm";
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `walkthrough-${stamp}.${ext}`, { type: mr.mimeType || "audio/webm" });
      await uploadAndTranscribe(file, pid);
    };

    try {
      // Ask for a data event every second so we always have something on stop.
      mr.start(1000);
      setRecStatus("recording");
      toast.info("Recording started — click Stop when you're done.");
    } catch (err) {
      try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      streamRef.current = null;
      mediaRecorderRef.current = null;
      setRecStatus("idle");
      setRecError("Recording could not start. Try again, or type the walkthrough below.");
      toast.error("Recording could not start.");
    }
  };

  const stopRecording = () => {
    if (recStatus !== "recording") return;
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    try {
      mr.stop();
      // Status stays "recording" until onstop fires; onstop bumps us into
      // "uploading" via uploadAndTranscribe.
      setRecStatus("uploading");
    } catch {
      setRecStatus("idle");
    }
  };

  const uploadAndTranscribe = async (file, projectId) => {
    setRecStatus("uploading");
    let noteId = null;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: upload } = await api.post(
        `/voice-notes/upload?project_id=${projectId}&source=recorded`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      noteId = upload?.id;
    } catch (err) {
      setRecStatus("idle");
      setRecError(err?.response?.data?.detail || "Could not upload the recording. Your existing notes are preserved — try again or type instead.");
      toast.error(err?.response?.data?.detail || "Voice upload failed.");
      return;
    }

    setRecStatus("transcribing");
    let transcript = "";
    try {
      const { data: tr } = await api.post(`/voice-notes/${noteId}/transcribe`);
      transcript = (tr?.transcript_edited || tr?.transcript_raw || "").trim();
      if (!transcript) {
        setRecStatus("idle");
        setRecError("The recording was uploaded but Whisper returned no text. Try recording again in a quieter spot, or type the walkthrough below.");
        toast.error("Transcription returned no text.");
        return;
      }
    } catch (err) {
      setRecStatus("idle");
      setRecError(err?.response?.data?.detail || "Transcription failed. The audio was saved to the project — you can retry from the Project page.");
      toast.error(err?.response?.data?.detail || "Transcription failed.");
      return;
    }

    // Merge the fresh transcript into the wizard field, preserving anything
    // the contractor already typed (append with a blank line separator).
    setForm((prev) => {
      const existing = (prev.voice_transcript || "").trim();
      const next = existing
        ? `${existing}\n\n${transcript}`
        : transcript;
      return { ...prev, voice_transcript: next };
    });
    setRecStatus("idle");
    setRecError("");
    toast.success("Transcript added to the walkthrough notes.");
  };

  // ---------------- Submit ----------------

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Slice 2J amendment · Edit Estimate path — no new project or
      // estimate is created. Reuse the existing project+estimate
      // identity; just PUT the updated project data and return to the
      // same estimate with the levi_ack flag so the centered
      // acknowledgement fires. Estimate identity is preserved.
      if (isEditMode) {
        await api.put(`/projects/${projectPrefillId}`, { ...form });
        submittedRef.current = true;
        clearDraft();
        navigate(`/app/estimates/${editEstimateId}?levi_ack=1`);
        return;
      }
      const projectId = await ensureDraftProject();
      await api.put(`/projects/${projectId}`, { ...form, status: "active" });
      const { data: estimate } = await api.post("/estimates", {
        project_id: projectId,
        // iter57 · Slice 1B — carry the Job linkage from the Job Detail
        // deep-link so the estimate lands under the correct Job.
        job_id: jobIdRef.current || "",
      });
      submittedRef.current = true;
      clearDraft();
      toast.success("Project & estimate created");
      navigate(`/app/estimates/${estimate.id}`);
    } catch (err) {
      toast.error(explainSaveError(err, "estimate"));
      setSubmitting(false);
    }
  };

  // ---------------- Render helpers ----------------

  const showStartBtn = recStatus === "idle";
  const showStopBtn = recStatus === "recording";
  const isBusyBtn = recStatus === "requesting" || recStatus === "uploading" || recStatus === "transcribing";
  const busyLabel =
    recStatus === "requesting" ? "Requesting mic…"
    : recStatus === "uploading" ? "Uploading…"
    : recStatus === "transcribing" ? "Transcribing…"
    : "";

  const focusFullEstimate = () => {
    const customerNameField = document.querySelector('[data-testid="estimate-customer-name"]');
    customerNameField?.focus();
    customerNameField?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };

  const topBackButton = step > 0 ? (
    <button
      type="button"
      onClick={goBack}
      data-testid={`estimate-top-back-btn-step${step + 1}`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] text-[var(--jc-text)]"
    >
      <ArrowLeft size={12} /> {isEditMode && step === 1 ? "Back to Customer + Job Basics" : `Back to Step ${step}`}
    </button>
  ) : null;

  return (
    <DashboardLayout
      title={isEditMode ? "Edit Estimate" : "New Estimate"}
      subtitle={isEditMode ? "Update the job information for this estimate." : "Capture the walkthrough so you can send a proposal fast."}
      actions={
        <button
          onClick={() => {
            if (formHasContent(form) && !window.confirm("Leave without saving? Your walkthrough will be discarded.")) return;
            clearDraft();
            navigate("/app");
          }}
          data-testid="estimate-wizard-cancel-btn"
          className="text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] inline-flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Cancel
        </button>
      }
    >
      <div className="max-w-4xl">
        <div
          className="flex items-center gap-2 mb-8 overflow-x-auto"
          data-testid="new-estimate-stepper"
        >
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={`new-estimate-step-pill-${i + 1}`}
              className="flex items-center gap-2 shrink-0"
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step ? "bg-[var(--jc-green)] text-white" : i === step ? "bg-[var(--jc-navy)] text-white" : "bg-[var(--jc-border)] text-[var(--jc-text-soft)]"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <div className={`text-xs font-semibold ${i === step ? "text-[var(--jc-text)]" : "text-[var(--jc-text-soft)]"}`}>{s.label}</div>
              {i < STEPS.length - 1 && <div className="w-6 h-px bg-[var(--jc-border)] mx-1" />}
            </div>
          ))}
        </div>

        {/* iter49 · in-app Back button lives at the TOP of Steps 2 & 3 so the
            contractor never reaches for the browser Back button and blows
            away their draft. */}
        {step > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3">
            {topBackButton}
            <p className="text-[11px] text-[var(--jc-text-soft)]" data-testid="estimate-draft-safe-note">
              Your draft is saved as you work — refresh, close the tab, or come back later.
            </p>
          </div>
        )}

        <div className="jc-card p-6 lg:p-8">
          {step === 0 && (
            <div className="space-y-4">
              {/* Slice 2E1 · Levi AI Estimate chooser — shown only when the
                  wizard has not been touched yet. Contractor can bail to
                  the lightweight AI flow instead of the structured
                  Approved Pricing walk-through. Draft-resume respects
                  jc:new-estimate-draft-v2 so refreshes don't lose work. */}
              {!(
                (form.customer_name || "").trim() ||
                (form.job_type || "").trim() ||
                (form.voice_transcript || "").trim() ||
                (form.typed_notes || "").trim()
              ) && (
                <div className="border border-[var(--jc-border)] rounded-lg bg-[var(--jc-bg)] p-5 space-y-3" data-testid="new-estimate-workflow-chooser">
                  <div>
                    <div className="font-display text-lg">{t("levi_estimate.chooser_heading")}</div>
                    <p className="text-xs text-[var(--jc-text-soft)] mt-1">{t("levi_estimate.chooser_help")}</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={focusFullEstimate}
                      aria-pressed="true"
                      className="w-full rounded-md border border-[var(--jc-blue)] bg-white p-4 flex flex-col text-left hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jc-blue)] focus-visible:ring-offset-2 transition-shadow"
                      data-testid="chooser-approved-pricing"
                    >
                      <div className="font-semibold text-sm text-[var(--jc-blue)]">{t("levi_estimate.chooser_approved_title")}</div>
                      <p className="text-xs text-[var(--jc-text-soft)] mt-1 flex-1">{t("levi_estimate.chooser_approved_body")}</p>
                      <div className="mt-3 text-[11px] font-semibold text-[var(--jc-blue)]">↓ {t("levi_estimate.chooser_approved_action")}</div>
                    </button>
                    <Link
                      to="/app/estimates/new/levi"
                      data-testid="chooser-levi"
                      className="rounded-md border border-[var(--jc-border)] bg-white p-4 flex flex-col hover:border-[var(--jc-blue)] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jc-blue)] focus-visible:ring-offset-2 transition-[border-color,box-shadow]"
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-sm">{t("levi_estimate.chooser_levi_title")}</div>
                      </div>
                      <p className="text-xs text-[var(--jc-text-soft)] mt-1 flex-1 whitespace-pre-line">{t("levi_estimate.chooser_levi_body")}</p>
                      <div className="mt-3 text-[11px] font-semibold text-[var(--jc-blue)]">→ {t("levi_estimate.chooser_levi_action")}</div>
                    </Link>
                  </div>
                  <p className="text-[11px] text-[var(--jc-text-soft)] leading-relaxed border-t border-[var(--jc-border)] pt-3" data-testid="chooser-levi-notice">
                    {t("levi_estimate.chooser_levi_notice")}
                  </p>
                </div>
              )}
              <div className="overline">Step 1 · Customer + Job Basics</div>
              <h2 className="font-display text-2xl">Who is this job for and what are we building?</h2>
              <p className="text-sm text-[var(--jc-text-soft)] -mt-2">
                Enter the basics. Full walk-through detail comes next.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Customer name" data-testid="estimate-customer-name" value={form.customer_name} onChange={u("customer_name")} placeholder="Jane Smith" />
                <Field label="Customer phone" data-testid="estimate-customer-phone" value={form.customer_phone} onChange={u("customer_phone")} placeholder="(555) 123-4567" />
                <Field label="Customer email" data-testid="estimate-customer-email" value={form.customer_email} onChange={u("customer_email")} placeholder="jane@example.com" />
                <Field label="Job address" data-testid="estimate-job-address" value={form.job_address} onChange={u("job_address")} placeholder="123 Main St, Springfield" />
                <Field label="Job type / Trade" data-testid="estimate-job-type" value={form.job_type} onChange={u("job_type")} placeholder="Bathroom remodel, Roof replacement..." />
              </div>
              <div data-testid="wizard-step2-summary-block">
                <TA
                  label="Short project summary"
                  rows={2}
                  value={form.job_description}
                  onChange={u("job_description")}
                  placeholder='One-line summary the customer will recognize (e.g. "Master-bath tile + LVP transition").'
                  data-testid="wizard-job-summary"
                />
                <p className="text-[11px] text-[var(--jc-text-soft)] mt-1.5" data-testid="wizard-step2-summary-helper">
                  Full job-walk details go in Step 2 → Scope / Job Walk Notes.
                </p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="overline">Step 2 · Scope / Job Walk Notes</div>
              <h2 className="font-display text-2xl">{isEditMode ? "Update the job information" : "Capture the job walk."}</h2>
              <p className="text-sm text-[var(--jc-text-soft)] -mt-2" data-testid="wizard-step3-helper">
                {t("estimates_form.wizard_step3_helper")}
              </p>

              {/* Slice VW-01 · Two primary capture choices. */}
              <div className="rounded-lg border border-[var(--jc-blue)]/40 bg-[var(--jc-surface)] p-4 space-y-3" data-testid="video-walk-primary-card">
                <div>
                  <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">
                    Recommended
                  </div>
                  <div className="text-lg font-semibold">Record a Video Job Walk</div>
                  <p className="text-sm text-[var(--jc-text-soft)] mt-1 max-w-xl">
                    Record the job. Describe what you see. Levi organizes the scope.
                  </p>
                </div>
                {draftProjectId ? (
                  <VideoWalkRecorder
                    projectId={draftProjectId}
                    embedded
                    autoStart={_autoCamera}
                    primaryCta={{
                      label: "Continue to Review Estimate",
                      onClick: () => setStep(2),
                    }}
                    onFinalized={(finalizedSession) => {
                      const text = (finalizedSession?.video_walk_transcript?.text || "").trim();
                      if (text) {
                        setForm((prev) => ({
                          ...prev,
                          voice_transcript: prev.voice_transcript?.trim()
                            ? `${prev.voice_transcript}\n\n---\n\n${text}`
                            : text,
                        }));
                      }
                    }}
                  />
                ) : (
                  <div className="rounded-md border border-dashed border-[var(--jc-border)] p-4 text-sm text-[var(--jc-text-soft)]" data-testid="video-walk-needs-draft-project">
                    Add a customer name in Step 1 first — the Video Job Walk needs a project to attach the recording to.
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 py-1" data-testid="video-walk-or-divider">
                <div className="flex-1 h-px bg-[var(--jc-border)]" />
                <div className="text-xs uppercase tracking-wide text-[var(--jc-text-soft)]">or enter details manually</div>
                <div className="flex-1 h-px bg-[var(--jc-border)]" />
              </div>

              <TA
                label={t("estimates_form.wizard_job_notes_label")}
                data-testid="wizard-job-notes-textarea"
                rows={6}
                value={form.typed_notes}
                onChange={u("typed_notes")}
                placeholder={t("estimates_form.wizard_job_notes_placeholder")}
              />
              <p className="text-[11px] text-[var(--jc-text-soft)] mt-1" data-testid="wizard-job-notes-helper">
                {t("estimates_form.wizard_job_notes_helper")}
              </p>


              {/* Slice 2T · Levi 1.0 Intake Coach — natural intake guidance.
                  Never blocks Next. Never reroutes out of intake. Never
                  fetches. Uses only local wizard state. */}
              <LeviIntakeCoach form={form} draftProjectId={draftProjectId} />

              <div className="rounded-lg border border-[var(--jc-border)] p-4 bg-white">
                <div className="flex items-center gap-2 mb-2">
                  <Camera size={14} className="text-[var(--jc-text-soft)]" />
                  <div className="overline">Job photos &amp; documents</div>
                </div>
                {draftProjectId ? (
                  <>
                    <p className="text-xs text-[var(--jc-text-soft)] mb-3">
                      Upload site photos, measurement sheets, and PDFs. Levi will use them when checking and helping with this estimate.
                    </p>
                    <PhotoUploader projectId={draftProjectId} />
                  </>
                ) : (
                  <p className="text-xs text-[var(--jc-text-soft)]">
                    Complete <b>Step 1 · Customer + Job Basics</b> first — your project will be created in draft so you can attach files here.
                  </p>
                )}
              </div>

              <div className="pt-4 mt-2 border-t border-[var(--jc-border)]" data-testid="job-intake-section">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  data-testid="wizard-advanced-intake-toggle"
                  aria-expanded={advancedOpen}
                  aria-controls="wizard-advanced-intake-panel"
                  className="w-full flex items-center justify-between gap-3 py-1 text-left hover:opacity-80"
                >
                  <div className="flex items-center gap-2">
                    <Settings2 size={14} className="text-[var(--jc-text-soft)]" />
                    <div className="overline">Show advanced details (optional — urgency, insurance, timeline, access notes)</div>
                  </div>
                  {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {advancedOpen && (
                  <div id="wizard-advanced-intake-panel" data-testid="wizard-advanced-intake-panel" className="mt-3 space-y-4">
                    <p className="text-xs text-[var(--jc-text-soft)]">
                      Optional. Helps you triage, schedule, and route the job. These fields stay
                      internal and never appear on customer proposals, contracts, invoices, or PDFs.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">Urgency</label>
                        <select
                          data-testid="intake-urgency"
                          value={form.urgency}
                          onChange={u("urgency")}
                          className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] bg-white"
                        >
                          <option value="">— Select —</option>
                          <option value="emergency">Emergency</option>
                          <option value="urgent">Urgent</option>
                          <option value="normal">Normal</option>
                          <option value="planned">Planned</option>
                        </select>
                      </div>
                      <Field
                        label="Service needed"
                        data-testid="intake-service-needed"
                        value={form.service_needed}
                        onChange={u("service_needed")}
                        placeholder="e.g. Roof leak, Water damage, Flooring, Drywall repair"
                      />
                      <div>
                        <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">Insurance claim?</label>
                        <select
                          data-testid="intake-insurance-claim"
                          value={form.insurance_claim}
                          onChange={u("insurance_claim")}
                          className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] bg-white"
                        >
                          <option value="">— Select —</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </div>
                      <Field
                        label="Lead source"
                        data-testid="intake-lead-source"
                        value={form.lead_source}
                        onChange={u("lead_source")}
                        placeholder="Referral, Website, Phone call, Repeat, Facebook, Google, Other"
                      />
                      {(form.insurance_claim === "yes" || form.insurance_claim === "unknown") && (
                        <>
                          <Field
                            label="Insurance carrier"
                            data-testid="intake-insurance-carrier"
                            value={form.insurance_carrier}
                            onChange={u("insurance_carrier")}
                            placeholder="State Farm, Allstate…"
                          />
                          <Field
                            label="Claim number"
                            data-testid="intake-claim-number"
                            value={form.insurance_claim_number}
                            onChange={u("insurance_claim_number")}
                            placeholder="Claim reference"
                          />
                        </>
                      )}
                      <div>
                        <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1.5">Preferred appointment</label>
                        <input
                          type="datetime-local"
                          data-testid="intake-preferred-appointment"
                          value={form.preferred_appointment_at}
                          onChange={u("preferred_appointment_at")}
                          className="w-full px-3.5 py-2.5 rounded-md border border-[var(--jc-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)]"
                        />
                      </div>
                    </div>
                    <TA
                      label="Access notes (internal)"
                      data-testid="intake-access-notes"
                      rows={2}
                      value={form.intake_access_notes}
                      onChange={u("intake_access_notes")}
                      placeholder="Gate code, lockbox, pets on property, meet on site, roof access, etc."
                    />

                    <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-[var(--jc-border)]">
                      <TA label="Material notes" value={form.material_notes} onChange={u("material_notes")} placeholder="Products, colors, brand preferences, contractor-supplied vs customer-supplied…" />
                      <TA label="Labor notes" value={form.labor_notes} onChange={u("labor_notes")} placeholder="Crew, subs, sequencing, tricky access, etc." />
                    </div>

                    <div className="pt-2 border-t border-[var(--jc-border)]">
                      <Field label="Timeline" value={form.timeline} onChange={u("timeline")} placeholder="e.g. Start in 2 weeks, complete in 10 days" />
                      <div className="grid md:grid-cols-2 gap-4 mt-3">
                        <TA label="Special conditions" value={form.special_conditions} onChange={u("special_conditions")} placeholder="Permit needs, HOA, occupancy during work, etc." />
                        <TA label="Customer concerns" value={form.customer_concerns} onChange={u("customer_concerns")} placeholder="Budget worries, timeline, kids/pets, dust control, etc." />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="overline">Step 3 · Review + Create Estimate</div>
              <h2 className="font-display text-2xl">Looks good?</h2>
              <p className="text-sm text-[var(--jc-text-soft)]">
                We&apos;ll {draftProjectId ? "promote your draft project to active" : "create the project"} and an empty estimate. On the next screen you can run <b>Organize Scope with AI</b> and the <b>AI Estimate Check</b>.
                {draftProjectId && " Any photos you uploaded are already attached."}
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm" data-testid="wizard-review-summary">
                <div><span className="text-[var(--jc-text-soft)]">Customer:</span> {form.customer_name}</div>
                <div><span className="text-[var(--jc-text-soft)]">Address:</span> {form.job_address || "—"}</div>
                <div><span className="text-[var(--jc-text-soft)]">Job type:</span> {form.job_type || "—"}</div>
                <div><span className="text-[var(--jc-text-soft)]">Timeline:</span> {form.timeline || "—"}</div>
                <div className="md:col-span-2"><span className="text-[var(--jc-text-soft)]">Job notes &amp; measurements:</span> {(form.typed_notes || form.measurements || "").trim() ? <span data-testid="wizard-review-job-notes-present">Captured ({(form.typed_notes?.length || 0) + (form.measurements?.length || 0)} characters)</span> : <span className="text-[var(--jc-text-soft)]">—</span>}</div>
                <div className="md:col-span-2">
                  <span className="text-[var(--jc-text-soft)]">Voice walkthrough:</span>{" "}
                  {form.voice_transcript?.trim() ? (
                    <span data-testid="wizard-review-walkthrough-present">Captured ({form.voice_transcript.length} characters)</span>
                  ) : (form.typed_notes?.trim() || form.measurements?.trim()) ? (
                    <span className="text-[var(--jc-text-soft)]" data-testid="wizard-review-walkthrough-typed-only">Typed notes only — no voice transcript captured.</span>
                  ) : (
                    <span className="text-red-700 font-semibold" data-testid="wizard-review-walkthrough-missing">
                      No walkthrough captured — Organize Scope with AI won&apos;t have anything to work with.
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--jc-border)]">
            <button
              onClick={goBack}
              disabled={step === 0}
              data-testid="estimate-bottom-back-btn"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] disabled:opacity-40"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={goNext}
                disabled={!canNext()}
                data-testid="estimate-next-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-50"
              >
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="estimate-create-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue disabled:opacity-60"
              >
                {submitting ? <><Loader2 className="animate-spin" size={14} /> Creating…</> : (
                  <>Create Project &amp; Estimate <ArrowRight size={14} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NewEstimate;
